<template>
  <div>
    <div class="box">
      <loading v-if="isLoading" />

      <div v-if="!isLoading">
        <div class="columns">
          <div class="column">
            <h2 class="title has-text-primary">
              <fa-icon class="margin-right-2" :icon="faChartNetwork" />Activity Group: '{{activityGroup.title}}'
            </h2>
          </div>
          <div class="column is-narrow">
            <div class="buttons">
              <button class="button is-primary is-outlined margin-right-2" @click="createActivity">
                <fa-icon :icon="faPlus" />Add
              </button>
              <custom-button modifier="primary" class="margin-right-2" :icon="faChevronLeft" iconPosition="left" @click="$router.push({ path: '/manage/activity-groups' })">Back</custom-button>
              <custom-button modifier="primary" :icon="faSyncAlt" iconPosition="left" @click="refresh">Refresh</custom-button>
            </div>
          </div>
        </div>

        <div class="panel">
          <div class="panel-heading">
            <div class="columns">
              <div class="column">
                <div class="margin-top-1">Filter Options</div>
              </div>
              <div class="column margin-top-1 is-narrow">{{filteredActivities.length}} result(s)</div>

              <div class="column is-narrow">
                <button class="button is-primary is-outlined" @click="resetFilters">
                  <fa-icon :icon="faUndo" />Reset Filters
                </button>
              </div>
            </div>
          </div>
          <div class="panel-block">
            <p class="control has-icons-left">
              <input class="input" v-model="searchTerm" placeholder="Search..." />
              <span class="icon is-left">
                <fa-icon :icon="faSearch" />
              </span>
            </p>
          </div>
        </div>

        <table class="table is-striped is-hoverable is-fullwidth" v-if="filteredActivities.length > 0">
          <thead>
          <tr>
            <th>Activity</th>
            <th class="is-narrow"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(activity, index) in filteredActivities" :key="index">
            <td>{{activity.title}}</td>
            <td class="is-narrow">
              <div class="buttons is-pulled-right is-inline-block">
                <custom-button class="is-inline-block margin-right-2" modifier="primary" :icon="faEdit" iconPosition="left" @click="editActivity(activity)">Edit</custom-button>
                <custom-button class="is-inline-block" modifier="danger" :icon="faTrashAlt" iconPosition="left" @click="deleteActivity(activity)">Delete</custom-button>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
        <div class="has-text-centered" v-if="activityGroup.activities.length === 0">No activities found.</div>
        <div class="has-text-centered" v-if="activityGroup.activities.length > 0 && filteredActivities.length === 0">No matching activities found.</div>
      </div>
    </div>

    <modal v-if="showEditActivityModal" :title="activityBeingEdited.id ? 'Edit Activity' : 'Create Activity'" @close="cancelEditingActivity" @submit="saveEditedActivity">
      <div class="field is-horizontal">
        <div class="field-label is-normal">
          <label class="label">Title</label>
        </div>
        <div class="field-body">
          <div class="field">
            <div class="control">
              <input class="input" :class="{'is-danger' : $v.activityBeingEdited.$dirty && $v.activityBeingEdited.title.$anyError}" type="text" v-model="$v.activityBeingEdited.title.$model" />
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.activityBeingEdited.$dirty && !$v.activityBeingEdited.title.required">{{$v.activityBeingEdited.title.$params.required.msg}}</div>
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.activityBeingEdited.$dirty && !$v.activityBeingEdited.title.maxLength">{{$v.activityBeingEdited.title.$params.maxLength.msg}}</div>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { shallowCopy } from "@/utilities"
import loading from "@/components/loading";
import customButton from "@/components/custom-button";
import modal from "@/components/modal";
import {faChartNetwork} from "@fortawesome/pro-solid-svg-icons";
import {
  faChevronLeft,
  faEdit,
  faPlus,
  faSearch,
  faSyncAlt,
  faTrashAlt,
  faUndo
} from "@fortawesome/free-solid-svg-icons";
import {withParams} from "vuelidate";
import {maxLength, required} from "vuelidate/lib/validators";

export default {
  components: {
    loading,
    customButton,
    modal
  },
  computed: {
    filteredActivities() {
      if (!this.activityGroup.activities || !this.activityGroup.activities.length)
        return [];

      if (!this.searchTerm || this.searchTerm === "")
        return this.activityGroup.activities;

      return this.activityGroup.activities.filter(x => x.title.toLowerCase().includes(this.searchTerm.toLowerCase()))
    }
  },
  data() {
    return {
      isLoading: true,
      activityGroup: {
        id: null,
        title: null,
        order: null,
        isTimeOff: null,
        isDeleted: null,
        activities: []
      },
      searchTerm: "",
      activityBeingEdited: {
        id: null,
        activityGroupId: null,
        title: null
      },
      showEditActivityModal: false,
      faChartNetwork,
      faSyncAlt,
      faSearch,
      faUndo,
      faEdit,
      faTrashAlt,
      faPlus,
      faChevronLeft
    }
  },
  methods: {
    refresh() {
      this.isLoading = true

      this.$http.get("ActivityGroups/" + this.$route.params.id).then(response => {
        this.activityGroup = response.data
        this.isLoading = false
      }).catch(() => {
        this.isLoading = false
        this.$alert("An error occurred loading the activity group.", "", "error");
      })
    },
    resetFilters() {
      this.searchTerm = "";
    },
    createActivity() {
      this.activityBeingEdited = {
        id: null,
        activityGroupId: parseInt(this.$route.params.id),
        title: null
      };

      this.$v.activityBeingEdited.$reset();

      this.showEditActivityModal = true;
    },
    editActivity(activity) {
      this.activityBeingEdited = shallowCopy(activity);
      this.showEditActivityModal = true;
    },
    saveEditedActivity() {
      this.$v.activityBeingEdited.$touch();

      if (this.$v.activityBeingEdited.$invalid)
        return;

      this.isLoading = true

      let data = {
        activityGroupId: this.activityBeingEdited.activityGroupId,
        title: this.activityBeingEdited.title
      };

      if (this.activityBeingEdited.id != null) {
        data.id = this.activityBeingEdited.id;

        this.$http.post("Activities", data).then(() => {
          this.showEditActivityModal = false;
          this.refresh();
          this.$alert("Activity updated successfully.", "", "success");
        }).catch((error) => {
          if (error.response.data.message) {
            this.$alert(error.response.data.message, "", "error");
          } else this.$alert("An error occurred updating the activity.", "", "error");

          this.isLoading = false
        });
      }
      else {
        this.$http.put("Activities", data).then(() => {
          this.showEditActivityModal = false;
          this.refresh();
          this.$alert("Activity created successfully.", "", "success");
        }).catch((error) => {
          if (error.response.data.message) {
            this.$alert(error.response.data.message, "", "error");
          } else this.$alert("An error occurred creating the activity.", "", "error");

          this.isLoading = false
        });
      }
    },
    cancelEditingActivity() {
      this.showEditActivityModal = false;
    },
    deleteActivity(activity) {
      this.$confirm("Are you sure you wish to delete the activity '" + activity.title + "'?", "Delete activity?", "question").then(() => {
        this.$http.delete("Activities/" + activity.id).then(() => {
          this.refresh();
          this.$alert("Activity deleted successfully.", "", "success");
        }).catch((error) => {
          if (error.response.data.message) {
            this.$alert(error.response.data.message, "", "error");
          } else this.$alert("An error occurred deleting the activity.", "", "error");
        });
      });
    }
  },
  created() {
    this.refresh()
  },
  validations: {
    activityBeingEdited: {
      title: {
        required: withParams({ msg: "Please enter a title." }, required),
        maxLength: withParams({ msg: "The title must be no longer than 50 characters." }, maxLength(50))
      }
    }
  }
}
</script>

<style scoped>

</style>